import { CONFIDENCE_RANK, type RecordCheckConfidence, type RecordCheckField, type RecordCheckMark, type RecordCheckUnknownReason } from '../records/recordSuggestions';

/**
 * 🔒 The validation study on a contacts read (piece 31; decisions 5382, 5631;
 * T2739). Staging writes each contacts row's study as Waiting in
 * `ImportStagedRow.suggestions`; the `recordChecks` rider writes Checked with
 * the marks, or NotChecked with its reason. The marks travel on the staged row,
 * because no record exists yet, and the row's write turns each into a
 * `RecordCheck` row on the record it wrote.
 */

export type StagedStudyState = 'Waiting' | 'Checked' | 'NotChecked';
export type StagedStudyReason = Extract<RecordCheckUnknownReason, 'CapReached' | 'NoAdapter'>;

/** 🔒 The compare's own rows a suggestion draws on (decision 5631): the name, the website and the main phone. */
export const STUDY_COMPARE_FIELDS: Readonly<Partial<Record<RecordCheckField, string>>> = {
  CompanyName: 'displayName',
  Website: 'website',
  MainPhone: 'mainPhoneNumber',
};

/** 🔒 A contact person's fields a row offers with their own boxes (decision 5631). */
export const STUDY_PERSON_FIELDS: readonly RecordCheckField[] = ['PersonName', 'Email', 'Phone', 'Title'];

/** One mark as the staged row holds it, keyed by its field and the contact person it names. */
export interface StagedMark {
  key: string;
  field: RecordCheckField;
  /** The Zoho contact person the mark names; null on the company's own fields. */
  contactPersonId: string | null;
  mark: RecordCheckMark;
  unknownReason: RecordCheckUnknownReason | null;
  /** The incoming value the mark compared. */
  valueHere: string | null;
  suggestedValue: string | null;
  /** 🔒 An EIN or a DUNS suggestion, held as an EncryptedField and never as digits (decision 3082). */
  suggestedSecret?: unknown;
  confidence: RecordCheckConfidence | null;
  source: string | null;
  sourceUrl: string | null;
  /** Whether its box was ticked, written at the row's Accept. */
  ticked?: boolean;
}

/** The row's study as `suggestions` holds it; empty on a row no study ran on. */
export interface StagedStudy {
  state?: StagedStudyState;
  reason?: StagedStudyReason;
  checkedAt?: string;
  marks?: StagedMark[];
}

/** The key a mark is chosen by: its field and the contact person it names. */
export function stagedMarkKey(field: RecordCheckField, contactPersonId: string | null): string {
  return `${field}:${contactPersonId ?? ''}`;
}

/** One suggestion as Step through draws it. */
export interface StagedSuggestionView {
  key: string;
  field: RecordCheckField;
  /** The compare row it sits on, or null for a contact person's line. */
  compareField: string | null;
  /** The contact person's name, on a person's line. */
  personLabel: string | null;
  incoming: string | null;
  suggested: string;
  sealed: boolean;
  confidence: RecordCheckConfidence;
  source: string;
  sourceUrl: string | null;
}

/** The study as a row's detail reads it. */
export interface StagedStudyView {
  state: StagedStudyState;
  /** The NotChecked line, or null. */
  line: string | null;
  /** The compare rows' suggestions, keyed by the compare field. */
  fields: Record<string, StagedSuggestionView>;
  /** The contact people's suggestions, listed under the compare. */
  people: StagedSuggestionView[];
}

export const STUDY_CAP_LINE = "Not checked: the month's spending cap is reached";
export const STUDY_NO_ADAPTER_LINE = 'Not checked: no outside lookup is switched on';

export function studyLineOf(study: StagedStudy): string | null {
  if (study.state !== 'NotChecked') return null;
  return study.reason === 'CapReached' ? STUDY_CAP_LINE : STUDY_NO_ADAPTER_LINE;
}

/** 🔒 A suggestion's box starts ticked only at High confidence (piece 31). */
export function suggestionTicksOf(suggestions: readonly Pick<StagedSuggestionView, 'key' | 'confidence' | 'sealed'>[]): Record<string, boolean> {
  return Object.fromEntries(suggestions.filter((one) => !one.sealed).map((one) => [one.key, one.confidence === 'High']));
}

/**
 * 🔒 A remembered Accept's suggestion boxes on a following row (decision
 * 5631): the follower takes its own suggestion on each compare field where the
 * first row took one, at its own confidence or higher.
 */
export function followedSuggestionKeys(leader: readonly StagedMark[], ticked: readonly string[], follower: readonly StagedMark[]): string[] {
  const out: string[] = [];
  for (const key of ticked) {
    const first = leader.find((mark) => mark.key === key);
    if (!first || first.contactPersonId !== null || !STUDY_COMPARE_FIELDS[first.field] || !first.confidence) continue;
    const own = follower.find((mark) => mark.key === key && mark.mark === 'Differs' && mark.suggestedValue);
    if (own?.confidence && CONFIDENCE_RANK[own.confidence] >= CONFIDENCE_RANK[first.confidence]) out.push(key);
  }
  return out;
}
